import { ResourceList, Stack } from "@shopify/polaris"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { round } from "services/units/round"
import { POST_TREATMENT_CONFIGS } from "configs/postTreatmentConfig"

const PostTreatmentResultOrganism = ({
  fileQuality,
  contextType = "assets",
}) => {
  const { t } = useTranslation()
  const config = POST_TREATMENT_CONFIGS[contextType]

  const FINAL_TOTAL_COUNT_ID = config.dataKeys.finalCount
  const DISCARDED_TYPES_ID = config.dataKeys.discardedCount
  const DISCARDED_NODES_ID = config.dataKeys.nodeDiscardedCount
  const BAD_LEASED_DATA_ID = config.dataKeys.leasingDiscardedCount
  const OTHER_DISCARDED_ID = config.dataKeys.otherDiscardedCount
  const VALID_ASSETS_ID = config.dataKeys.validAssetsCount
  const ASSETS_DISCARDED_ID = config.dataKeys.assetsDiscardedCount

  const items = useCallback(() => {
    const baseItems = [
      {
        id: FINAL_TOTAL_COUNT_ID,
        title: t(`${config.translationPrefix}.final_total_count`),
        amount: fileQuality?.[config.dataKeys.finalCount],
        somme: config.showAmount
          ? fileQuality?.[config.dataKeys.finalAmount]
          : null,
      },
      {
        id: DISCARDED_TYPES_ID,
        title: t(
          `${config.translationPrefix}.${config.dataKeys.discardedCount}`
        ),
        amount: fileQuality?.[config.dataKeys.discardedCount],
        somme: config.showAmount
          ? fileQuality?.[config.dataKeys.discardedAmount]
          : null,
      },
    ]

    if (VALID_ASSETS_ID) {
      baseItems.push({
        id: VALID_ASSETS_ID,
        title: t(`${config.translationPrefix}.${VALID_ASSETS_ID}`),
        amount: fileQuality?.[VALID_ASSETS_ID],
        somme: null,
      })
    }

    if (ASSETS_DISCARDED_ID) {
      baseItems.push({
        id: ASSETS_DISCARDED_ID,
        title: t(`${config.translationPrefix}.${ASSETS_DISCARDED_ID}`),
        amount: fileQuality?.[ASSETS_DISCARDED_ID],
        somme: null,
      })
    }

    if ((fileQuality?.[config.dataKeys.nodeDiscardedCount] ?? 0) > 1)
      baseItems.push({
        id: DISCARDED_NODES_ID,
        title: t(
          `${config.translationPrefix}.${config.dataKeys.nodeDiscardedCount}`
        ),
        amount: fileQuality?.[config.dataKeys.nodeDiscardedCount],
        somme: config.showAmount
          ? fileQuality?.[config.dataKeys.nodeDiscardedAmount]
          : null,
        hidden: (fileQuality?.[config.dataKeys.nodeDiscardedCount] ?? 0) === 0,
      })

    return baseItems
  }, [
    fileQuality,
    config,
    t,
    FINAL_TOTAL_COUNT_ID,
    DISCARDED_TYPES_ID,
    DISCARDED_NODES_ID,
    VALID_ASSETS_ID,
    ASSETS_DISCARDED_ID,
  ])

  const invalid_subitems = useCallback(() => {
    const baseItems = []

    if ((fileQuality?.[config.dataKeys.leasingDiscardedCount] ?? 0) > 0)
      baseItems.push({
        id: BAD_LEASED_DATA_ID,
        title: t(
          `${config.translationPrefix}.${config.dataKeys.leasingDiscardedCount}`
        ),
        amount: fileQuality?.[config.dataKeys.leasingDiscardedCount],
        somme: config.showAmount
          ? fileQuality?.[config.dataKeys.leasingDiscardedAmount]
          : null,
      })

    if ((fileQuality?.[config.dataKeys.otherDiscardedCount] ?? 0) > 0)
      baseItems.push({
        id: OTHER_DISCARDED_ID,
        title: t(
          `${config.translationPrefix}.${config.dataKeys.otherDiscardedCount}`
        ),
        amount: fileQuality?.[config.dataKeys.otherDiscardedCount],
        somme: config.showAmount
          ? fileQuality?.[config.dataKeys.otherDiscardedAmount]
          : null,
      })

    return baseItems
  }, [fileQuality, config, t, BAD_LEASED_DATA_ID, OTHER_DISCARDED_ID])

  const discardedEquipments = useCallback(
    () =>
      fileQuality?.[config.discardedDataKeys.main]
        ?.map(
          (item) =>
            `${item?.[config.itemLabelKey]} (${round(item?.percent ?? 0)}%)`
        )
        .join(", "),
    [fileQuality, config]
  )

  const discardedLeasingEquipments = useCallback(
    () =>
      fileQuality?.[config.discardedDataKeys.leasing]
        ?.map((item) => {
          const percentage = `(${round(item?.percent ?? 0)}%)`
          const reason = item?.reason
            ? ` - ${t(
                `${config.translationPrefix}.leasing_missing_reason.${item.reason}`
              )}`
            : ""
          return `${item?.[config.itemLabelKey]} ${percentage}${reason}`
        })
        .join(", "),
    [fileQuality, config]
  )

  const discardedOtherEquipments = useCallback(
    () =>
      fileQuality?.[config.discardedDataKeys.other]
        ?.map(
          (item) =>
            `${item?.[config.itemLabelKey]} (${round(item?.percent ?? 0)}%)`
        )
        .join(", "),
    [fileQuality, config]
  )

  const discardedNodes = useCallback(
    () =>
      fileQuality?.[config.discardedDataKeys.nodes]
        ?.map(
          (node) =>
            `${node?.node_path || node?.supplier_name} (${node?.percent}%)`
        )
        .join(", "),
    [fileQuality, config]
  )

  const renderPurchasedItServicesDiscarded = useCallback(() => {
    if (contextType !== "purchases") return null
    const discarded = fileQuality?.service_it_purchased_discarded
    if (!Array.isArray(discarded) || discarded.length === 0) return null

    const nonZero = discarded.filter(
      (item) => (item.count ?? 0) > 0 || (item.amount ?? 0) > 0
    )
    if (nonZero.length === 0) return <span>0</span>

    return (
      <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
        {nonZero.map((item) => (
          <li key={item.type}>
            <b>{t(`collect:qualities.purchases.analytics.${item.type}`)}</b>
            {` : ${item.count}`}
            {item.amount !== undefined && ` (${item.amount} k€)`}
          </li>
        ))}
      </ul>
    )
  }, [fileQuality, t, contextType])

  // Per-reason breakdown of discarded websites (missing url / type / yearly visitors).
  const renderWebsitesDiscarded = useCallback(() => {
    if (contextType !== "websites") return null
    const discarded = fileQuality?.website_discarded
    if (!Array.isArray(discarded) || discarded.length === 0) return null

    const nonZero = discarded.filter((item) => (item.count ?? 0) > 0)
    if (nonZero.length === 0) return null

    return (
      <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
        {nonZero.map((item) => (
          <li key={item.type}>
            <b>{t(`collect:qualities.websites.analytics.${item.type}`)}</b>
            {` : ${item.count}`}
          </li>
        ))}
      </ul>
    )
  }, [fileQuality, t, contextType])

  // Per-reason breakdown of discarded external apps (missing type / numeric data).
  const renderExternalAppsDiscarded = useCallback(() => {
    if (contextType !== "external_apps") return null
    const discarded = fileQuality?.external_apps_discarded
    if (!Array.isArray(discarded) || discarded.length === 0) return null

    const nonZero = discarded.filter((item) => (item.count ?? 0) > 0)
    if (nonZero.length === 0) return null

    return (
      <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
        {nonZero.map((item) => (
          <li key={item.type}>
            <b>{t(`collect:qualities.external_apps.analytics.${item.type}`)}</b>
            {` : ${item.count}`}
          </li>
        ))}
      </ul>
    )
  }, [fileQuality, t, contextType])

  const renderInvalidPueDetail = useCallback(() => {
    if (!config.showInvalidPueDetail) return null
    const count = fileQuality?.invalid_pue_count ?? 0
    if (count <= 0) return null

    return (
      <p className="text text--italic">
        {t("collect:qualities.datacenter.invalid_pue")} : {count}
      </p>
    )
  }, [config.showInvalidPueDetail, fileQuality, t])

  return (
    <ResourceList
      items={items()}
      renderItem={({ id, title, amount, somme }) => (
        <ResourceList.Item id={id}>
          <Stack>
            <Stack.Item fill>
              <b>{title}</b>
            </Stack.Item>
            <Stack.Item>
              {config.showAmount && somme !== null
                ? `${amount || 0} (${somme || 0} k€)`
                : amount || 0}
            </Stack.Item>
          </Stack>

          <br />

          <p className="text text--italic">
            {id === DISCARDED_TYPES_ID && discardedEquipments()}
            {id === DISCARDED_NODES_ID && discardedNodes()}
            {id === BAD_LEASED_DATA_ID && discardedLeasingEquipments()}
            {id === DISCARDED_TYPES_ID && renderPurchasedItServicesDiscarded()}
            {id === DISCARDED_TYPES_ID && renderWebsitesDiscarded()}
            {id === DISCARDED_TYPES_ID && renderExternalAppsDiscarded()}
          </p>

          {id === DISCARDED_TYPES_ID && renderInvalidPueDetail()}

          <br />

          {id === DISCARDED_TYPES_ID &&
            !invalid_subitems().empty &&
            invalid_subitems().map((subItem) => (
              <div key={subItem.id} style={{ paddingLeft: "2rem" }}>
                <Stack>
                  <Stack.Item fill>
                    <b>{subItem.title}</b>
                  </Stack.Item>
                  <Stack.Item>
                    {config.showAmount && subItem.somme !== null
                      ? `${subItem.amount || 0} (${subItem.somme || 0} k€)`
                      : subItem.amount || 0}
                  </Stack.Item>
                </Stack>

                <br />

                <p className="text text--italic">
                  {subItem.id === BAD_LEASED_DATA_ID &&
                    discardedLeasingEquipments()}
                  {subItem.id === OTHER_DISCARDED_ID &&
                    discardedOtherEquipments()}
                </p>

                <br />
              </div>
            ))}
        </ResourceList.Item>
      )}
    />
  )
}

PostTreatmentResultOrganism.propTypes = {
  fileQuality: PropTypes.object.isRequired,
  contextType: PropTypes.string,
}

export default PostTreatmentResultOrganism
